import { Component, Inject, viewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ActionTypes,
  AddOwnerActions,
  IsolateResourceActions,
  RemoveAccessActions,
  RemovePermissionsActions,
} from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookActionDefinitionPropertiesBase } from 'src/app/models/api/playbooks/playbook.model';
import {
  PlaybooksActionDefinitionInput,
  PlaybooksActionInput,
  PlaybookSupportedActions,
} from 'src/app/models/componentInputs/playbookActionInput.interface';
import { PlaybooksActionOutput } from 'src/app/models/popups/outputs/playbookActionOutput.interface';
import { buildPlaybookActionTypeDisplayName } from '../../../labels/utils';
import { BehaviorSubject } from 'rxjs';
import { signal } from '@angular/core';
import { SetActionStatusActionComponent } from './actions/set-action-status-action/set-action-status-action.component';
import { SetAuthenticationModeComponent } from './actions/set-authentication-mode/set-authentication-mode.component';
import { SetConfirmationModeComponent } from './actions/set-confirmation-mode/set-confirmation-mode.component';
import {
  ActionStatus,
  PowerplatformAgentActionAuthenticationMode,
  PowerplatformAgentActionConfirmationMode,
  PowerplatformAgentActionSetAuthenticationModePayload,
  PowerplatformAgentActionSetConfirmationModePayload,
} from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';

interface ActionOptions {
  name: string;
  type: ActionTypes;
  tooltip?: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-playbook-action',
  templateUrl: './playbook-action.component.html',
  styleUrls: ['./playbook-action.component.scss'],
})
export class PlaybookActionComponent {
  public playbookAction?: ActionTypes;
  public selectedAction?: ActionTypes;

  public actionTypes = ActionTypes;

  public fg = new UntypedFormGroup({});
  public actionOptions: ActionOptions[] = [];

  public actionProperties?: PlaybookActionDefinitionPropertiesBase;
  public actionFormInput: PlaybooksActionDefinitionInput;
  public limitedActionText?: string;
  private readonly _supportedActions: PlaybookSupportedActions[];

  public showAddOwnerAction$ = new BehaviorSubject<boolean>(false);
  public showRemovePermissionsAction$ = new BehaviorSubject<boolean>(false);
  public showIsolateResourceAction$ = new BehaviorSubject<boolean>(false);
  public showRemoveAccessAction$ = new BehaviorSubject<boolean>(false);

  public setActionStatusActionComponent = viewChild(SetActionStatusActionComponent);
  public setAuthenticationModeActionComponent = viewChild(SetAuthenticationModeComponent);
  public setConfirmationModeActionComponent = viewChild(SetConfirmationModeComponent);

  public actionStatus = signal<ActionStatus>(
    (this.actionInput.action?.actionType
      ? this.actionInput.action?.actionType === ActionTypes.enableAgentAction
        ? ActionStatus.enabled
        : ActionStatus.disabled
      : ActionStatus.enabled) ?? ActionStatus.enabled
  );
  public authenticationMode = signal<PowerplatformAgentActionAuthenticationMode>(
    (this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetAuthenticationModePayload)
      ?.authenticationMode ?? PowerplatformAgentActionAuthenticationMode.invoker
  );
  public confirmationMode = signal<PowerplatformAgentActionConfirmationMode>(
    (this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetConfirmationModePayload)
      ?.confirmationMode ?? PowerplatformAgentActionConfirmationMode.strict
  );

  constructor(
    public dialogRef: MatDialogRef<PlaybookActionComponent>,
    @Inject(MAT_DIALOG_DATA) public actionInput: PlaybooksActionInput
  ) {
    this._supportedActions = actionInput.supportedActions;
    this.limitedActionText = actionInput.limitedActionsTooltip;
    this._createActionOptions();

    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeWindow();
    });

    actionInput.action?.let((action) => {
      if ([ActionTypes.enableAgentAction, ActionTypes.disableAgentAction].includes(action.actionType)) {
        this.selectedAction = ActionTypes.setActionStatus;
        this.playbookAction = ActionTypes.setActionStatus;
      } else {
        this.selectedAction = action.actionType;
        this.playbookAction = action.actionType;
      }
      this.actionProperties = action.actionDefinitionProperties;

      this.actionFormInput = {
        platform: this.actionInput.platform,
        actionType: action.actionType,
        actionDefinitionProperties: action.actionDefinitionProperties,
      } as PlaybooksActionDefinitionInput;
    });

    this.updateActionVisibility(actionInput.action?.actionType);
  }

  public onActionSelected(action: ActionOptions) {
    this.fg = new UntypedFormGroup({});
    this._resetActionOnChange();

    if (action.disabled) return;

    this.actionFormInput = {
      platform: this.actionInput.platform,
      actionType: action.type,
      actionDefinitionProperties: this.actionProperties,
    } as PlaybooksActionDefinitionInput;

    this.selectedAction = action.type;
    this.updateActionVisibility(action.type);
  }

  public closeWindow() {
    this.dialogRef.close({} as PlaybooksActionOutput);
  }

  public createOrUpdate() {
    let selectedValue = null;

    let selectedAction = this.selectedAction;

    if (this.setActionStatusActionComponent()) {
      selectedValue = this.setActionStatusActionComponent()?.actionStatus();

      if (this.selectedAction === ActionTypes.setActionStatus) {
        selectedAction =
          selectedValue === ActionStatus.enabled ? ActionTypes.enableAgentAction : ActionTypes.disableAgentAction;
      }

      this.fg.addControl('actionStatus', new UntypedFormControl(selectedValue));
    } else if (this.setAuthenticationModeActionComponent()) {
      selectedValue = this.setAuthenticationModeActionComponent()?.authenticationMode();
      this.fg.addControl('authenticationMode', new UntypedFormControl(selectedValue));
    } else if (this.setConfirmationModeActionComponent()) {
      selectedValue = this.setConfirmationModeActionComponent()?.confirmationMode();
      this.fg.addControl('confirmationMode', new UntypedFormControl(selectedValue));
    }

    this.dialogRef.close({
      action: {
        actionType: selectedAction,
        actionDefinitionProperties:
          this.selectedAction !== ActionTypes.setActionStatus
            ? (this.fg.value as PlaybookActionDefinitionPropertiesBase)
            : {},
      },
    } as PlaybooksActionOutput);
  }

  private updateActionVisibility(action?: ActionTypes) {
    const isAddOwnerActionVisible = action != undefined && AddOwnerActions.includes(action);
    const isRemovePermissionsVisible = action != undefined && RemovePermissionsActions.includes(action);
    const isIsolateResourceActionVisible = action != undefined && IsolateResourceActions.includes(action);
    const isRemoveAccessActionVisible = action != undefined && RemoveAccessActions.includes(action);

    this.showAddOwnerAction$.next(isAddOwnerActionVisible);
    this.showRemovePermissionsAction$.next(isRemovePermissionsVisible);
    this.showIsolateResourceAction$.next(isIsolateResourceActionVisible);
    this.showRemoveAccessAction$.next(isRemoveAccessActionVisible);
  }

  private _createActionOptions() {
    let actionOptions = this._supportedActions.map(
      (action) =>
        ({
          type: action.actionType,
          name: buildPlaybookActionTypeDisplayName(action.actionType),
          tooltip: action.tooltip,
          disabled: action.disabled,
        }) as ActionOptions
    );

    const agentActionAuthenticationMode = actionOptions.find(
      (action) => action.type === ActionTypes.setAgentActionAuthenticationMode
    );

    if (agentActionAuthenticationMode) {
      agentActionAuthenticationMode.name = 'Set User Authentication';
    }

    const agentActionConfirmationMode = actionOptions.find(
      (action) => action.type === ActionTypes.setAgentActionConfirmationMode
    );

    if (agentActionConfirmationMode) {
      agentActionConfirmationMode.name = 'Set User Confirmation';
    }

    this.actionOptions = structuredClone(actionOptions);

    const selection =
      this.playbookAction?.let((currentAction) =>
        this.actionOptions.find((options) => options.type === currentAction)
      ) ?? this.actionOptions[0];

    this.onActionSelected(selection);
  }

  private _resetActionOnChange() {
    this.selectedAction = undefined;
    this.playbookAction = undefined;
    this.actionProperties = undefined;
  }
}
